import type {
  ApplyResult,
  ApprovalRunOrigin,
  BulkRejectResultRow,
  InsightActionOrigin,
  RunApprovalItem,
  RunApprovals,
  RunDetail,
  RunDetailItem,
  RunHistoryPage,
  RunItemView,
  RunListRow,
} from '../models/insightAction.types'
import type {
  ApplyResponseDto,
  BulkLookupOriginDto,
  BulkLookupResponseDto,
  BulkRejectResponseDto,
  LookupOriginDto,
  OffsetPageDto,
  RunApprovalsDto,
  RunDetailDto,
  RunDetailItemDto,
  RunListRowDto,
} from '../models/insightAction.wire'

import { adaptActor, adaptCounts } from './adaptPreview'

export function adaptApplyResult(dto: ApplyResponseDto): ApplyResult {
  return {
    runId: dto.run_id,
    status: dto.status,
    alreadyExists: Boolean(dto.already_exists),
    totalItems: dto.total_items,
    counts: adaptCounts(dto.counts),
    items: (dto.items ?? []).map((item) => ({
      rowKey: item.row_key,
      status: item.status,
      reasonCode: item.reason_code,
      errorMessage: '',
      id: null,
      summary: '',
    })),
  }
}

export function adaptRunDetailItem(dto: RunDetailItemDto): RunDetailItem {
  return {
    id: dto.id,
    rowKey: dto.row_key,
    actionType: dto.action_type,
    platform: dto.platform,
    marketingChannel: dto.marketing_channel,
    targetLevel: dto.target_level,
    campaignId: dto.campaign_id ?? null,
    targetId: dto.target_id ?? null,
    status: dto.status,
    reasonCode: dto.reason_code,
    errorMessage: dto.error_message ?? '',
    summary: dto.row_snapshot?.summary_tr ?? '',
    columns: dto.row_snapshot?.columns ?? {},
    platformActionRequestId: dto.platform_action_request_id ?? null,
    cancellable: dto.cancellable === true,
    revertRequestId: dto.revert_request_id ?? null,
    revertible: Boolean(dto.revertible),
    revertBlockedReason: dto.revert_blocked_reason ?? null,
  }
}

export function adaptRunDetail(dto: RunDetailDto): RunDetail {
  return {
    runId: dto.run_id,
    insightId: dto.insight_id,
    status: dto.status,
    dataKey: dto.data_key,
    totalItems: dto.total_items,
    counts: adaptCounts(dto.counts),
    items: (dto.items ?? []).map((item) => adaptRunDetailItem(item)),
  }
}

export function runDetailItemsAsView(detail: RunDetail): RunItemView[] {
  return detail.items.map((item) => ({
    rowKey: item.rowKey,
    status: item.status,
    reasonCode: item.reasonCode,
    errorMessage: item.errorMessage,
    id: item.id,
    summary: item.summary,
  }))
}

export function adaptRunListRow(dto: RunListRowDto): RunListRow {
  return {
    runId: dto.run_id,
    insightId: dto.insight_id,
    groupKey: dto.group_key,
    surface: dto.surface,
    status: dto.status,
    requestedBy: adaptActor(dto.requested_by),
    totalItems: dto.total_items,
    counts: adaptCounts(dto.counts),
    createdAt: dto.created_at,
    finishedAt: dto.finished_at ?? null,
  }
}

export function adaptRunHistory(dto: OffsetPageDto<RunListRowDto>): RunHistoryPage {
  const runs = (dto.results ?? []).map((row) => adaptRunListRow(row))
  return { runs, total: dto.count ?? runs.length }
}

export function adaptOrigin(dto: LookupOriginDto | null): InsightActionOrigin | null {
  if (!dto) return null
  return {
    insightId: dto.insight_id,
    insightTitle: dto.insight_title,
    configLabel: dto.config_label,
    runId: dto.run_id,
    requestedBy: adaptActor(dto.requested_by),
    rowKey: dto.row_key,
    rowSummary: dto.row_snapshot?.summary_tr ?? '',
  }
}

export function adaptBulkOrigin(dto: BulkLookupOriginDto): ApprovalRunOrigin {
  return {
    runId: dto.run_id,
    insightId: dto.insight_id,
    insightTitle: dto.insight_title,
    configLabel: dto.config_label,
    groupKey: dto.group_key,
    rowSummary: dto.row_summary ?? '',
    itemId: dto.item_id,
    pendingTotal: dto.pending_total ?? 0,
  }
}

/** Keys come back in the raw form they were submitted; `null` = no insight opened that request. */
export function adaptBulkLookup(
  dto: BulkLookupResponseDto | null | undefined,
): Record<string, ApprovalRunOrigin | null> {
  return Object.fromEntries(
    Object.entries(dto ?? {}).map(([requestId, origin]) => [
      requestId,
      origin ? adaptBulkOrigin(origin) : null,
    ]),
  )
}

function adaptApprovalItem(dto: RunApprovalsDto['items'][number]): RunApprovalItem {
  return {
    itemId: dto.item_id,
    rowKey: dto.row_key,
    actionType: dto.action_type,
    platform: dto.platform,
    rowSummary: dto.row_summary ?? '',
    requiredRole: dto.policy?.required_role ?? null,
    canApprove: Boolean(dto.can_approve),
  }
}

export function adaptRunApprovals(dto: RunApprovalsDto): RunApprovals {
  return {
    runId: dto.run_id,
    requestedBy: adaptActor(dto.requested_by),
    items: (dto.items ?? []).map((item) => adaptApprovalItem(item)),
  }
}

export function adaptRejectResults(dto: BulkRejectResponseDto): BulkRejectResultRow[] {
  return (dto.results ?? []).map((row) => ({
    itemId: row.item_id,
    rowKey: row.row_key,
    status: row.status,
    reasonCode: row.reason_code,
  }))
}
